import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { AppNav } from '../components/AppNav';
import { NoAgencyBanner } from '../components/NoAgencyBanner';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, MapPin, BedDouble, Bath, Maximize2, Building2 } from 'lucide-react';

interface Property {
  id: string;
  title: string;
  price: number;
  currency: string;
  address: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  created_at: string;
  property_type?: string;
}

function PropertyRowSkeleton() {
  return (
    <div className="border border-border rounded-xl p-4 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1 mr-4">
          <div className="h-4 bg-muted rounded w-2/3" />
          <div className="h-3 bg-muted rounded w-1/2" />
        </div>
        <div className="flex gap-2">
          <div className="w-8 h-8 bg-muted rounded-lg" />
          <div className="w-8 h-8 bg-muted rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function PropertiesAdminPage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [noAgency, setNoAgency] = useState(false);

  const load = () => {
    setLoading(true);
    api.post('/properties/search', { limit: 100 })
      .then((r) => setProperties(r.data?.items ?? r.data ?? []))
      .catch((err) => {
        if (err?.response?.status === 403) {
          setNoAgency(true);
        } else {
          setError('No se pudieron cargar las propiedades');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await api.delete(`/properties/${id}`);
      setProperties((prev) => prev.filter((p) => p.id !== id));
    } catch {
      setError('Error al eliminar la propiedad');
    } finally {
      setDeleting(null);
    }
  };

  if (noAgency) return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <NoAgencyBanner />
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Propiedades</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {loading ? '—' : `${properties.length} propiedad${properties.length !== 1 ? 'es' : ''}`}
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => navigate('/admin/properties/new')}
            className="gap-1.5 active:scale-[0.97] transition-transform"
          >
            <Plus className="w-4 h-4" />
            Nueva
          </Button>
        </div>

        {error && <p className="text-sm text-destructive mb-4">{error}</p>}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => <PropertyRowSkeleton key={i} />)}
          </div>
        ) : properties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium">Sin propiedades</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Agrega tu primera propiedad al catálogo.</p>
            <Button onClick={() => navigate('/admin/properties/new')} className="gap-1.5">
              <Plus className="w-4 h-4" />Nueva propiedad
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {properties.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-4 py-4 group hover:bg-muted/30 px-3 -mx-3 rounded-xl transition-colors"
              >
                {/* Accent dot */}
                <div className="w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0 group-hover:bg-primary transition-colors" />

                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">
                    {p.title}
                    {p.property_type && p.property_type !== 'casa' && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md font-medium capitalize ml-1.5">
                        {p.property_type}
                      </span>
                    )}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span className="truncate">{p.address}</span>
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                  <span className="flex items-center gap-0.5">
                    <BedDouble className="w-3 h-3" />{p.bedrooms}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Bath className="w-3 h-3" />{p.bathrooms}
                  </span>
                  <span className="flex items-center gap-0.5">
                    <Maximize2 className="w-3 h-3" />{p.area} m²
                  </span>
                </div>

                <span className="text-sm font-semibold text-primary shrink-0 tabular-nums">
                  ${p.price.toLocaleString('es-MX')}
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">{p.currency}</span>
                </span>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => navigate(`/admin/properties/${p.id}/edit`)}
                    aria-label="Editar propiedad"
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`¿Eliminar "${p.title}"? Esta acción no se puede deshacer.`)) {
                        handleDelete(p.id);
                      }
                    }}
                    disabled={deleting === p.id}
                    aria-label="Eliminar propiedad"
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors disabled:opacity-40"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default PropertiesAdminPage;
